import { useCallback, useMemo, useState } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { TableReportTransactionsColumnsDefinition } from "./components/TableReportTransactionsColumnsDefinition";
import { format } from "date-fns";
import { StatusEnum } from "@/app/utils/status";
import { useReportTransactions } from "@/app/hooks/useReportTransactions";
import {
  ReportTransaction,
  ReportTransactionParams,
} from "@/app/services/reportService/reportTransactions";
import { reportService } from "@/app/services/reportService";
import * as XLSX from "xlsx";
import { useAuth } from "@/app/hooks/useAuth";
import { FiltersType } from "../components/FilterReports";
import { fetchAllReportData } from "@/app/utils/fetchAllReportData";
import { useToast } from "@/view/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { configService } from "@/app/services/configService";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";

const statusMap = {
  [StatusEnum.COMPLETED]: 'Pago',
  [StatusEnum.PENDING]: 'Em andamento',
  [StatusEnum.REFUSED]: 'Recusado',
  [StatusEnum.ACTION_REQUIRED]: 'Ação necessária',
  [StatusEnum.REFUNDED]: 'Reembolso',
  [StatusEnum.FINALIZED]: 'Finalizado',
}

const AMOUNT_REPORTS_FETCH =
  Number(import.meta.env.VITE_AMOUNT_REPORTS_FETCH) || 1000;

export const useReportTransactionsController = () => {
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const { data, isFetching, isLoading, setParams, params } =
    useReportTransactions();
  const { isLeader, isSeller, isAdmin, isManager, isSupervisor } = useAuth();
  const { toast } = useToast();

  const { data: productsCatalog } = useQuery<ProductCatalogItem[]>({
    queryKey: ["productsCatalog"],
    queryFn: configService.getProductsCatalog,
  });

  const { columns } = TableReportTransactionsColumnsDefinition(productsCatalog);

  const table = useReactTable({
    data: data?.transactions || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  });

  const fetchAllData = useCallback(async (): Promise<ReportTransaction[]> => {
    if (!data) return [];

    setIsExporting(true);
    setExportProgress(0);

    try {
      const allData = await fetchAllReportData<ReportTransaction>({
        total: data.pagination.total,
        batchSize: AMOUNT_REPORTS_FETCH,
        onProgress: setExportProgress,
        fetchBatch: async ({ skip, limit }) => {
          const exportParams: ReportTransactionParams = {
            ...params,
            limit,
            skip,
          };
          const batchData = await reportService.reportTransaction(exportParams);
          return batchData.transactions;
        },
      });

      allData.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );

      return allData;
    } finally {
      setIsExporting(false);
    }
  }, [data, params]);

  const handleExportToExcel = useCallback(async () => {
    try {
      const fetchedData = await fetchAllData();

      const baseFormattedData = fetchedData.map((transaction) => {
        const sellerTransactional = transaction.commissionList?.find(
          (c) => c.name === "seller" && c.type === "TRANSACTIONAL",
        )?.value;

        const sellerBonus = transaction.commissionList?.find(
          (c) => c.name === "seller" && c.type === "BONUS",
        )?.value;

        const leaderCommission = transaction.commissionList?.find(
          (c) => c.name === "leader",
        )?.value;

        const supervisorCommission = transaction.commissionList?.find(
          (c) => c.name === "supervisor",
        )?.value;

        return {
          seller: transaction.seller || "",
          team: transaction.team || "Sem equipe",
          manager: transaction.manager || "Sem gerente",
          supervisor: transaction.supervisor || "Sem supervisor",
          leader: transaction.leader || "Sem Líder",
          value: +(transaction.value / 100).toFixed(2) || 0,
          commission: +(transaction.commission / 100).toFixed(2) || 0,
          sellerTransactional: +(sellerTransactional ?? 0) / 100,
          sellerBonus: +(sellerBonus ?? 0) / 100,
          leaderCommission: +(leaderCommission ?? 0) / 100,
          supervisorCommission: +(supervisorCommission ?? 0) / 100,
          client: transaction.client || "",
          contract: transaction.contract || "",
          status: statusMap[transaction.status] || "Status desconhecido",
          table: transaction.table || "",
          date: format(transaction.createdAt, "dd/MM/yyyy, HH:mm") || "",
          updated: format(transaction.updatedAt, "dd/MM/yyyy, HH:mm") || "",
        };
      });

      const formattedData = isSeller
        ? baseFormattedData.map(({ leaderCommission: _lc, ...rest }) => rest)
        : baseFormattedData;

      const titles = [
        "Vendedor",
        "Equipe",
        "Gerente",
        "Supervisor",
        "Líder",
        "Valor",
        "Comissão Total",
        "Comissão Vendedor",
        "Comissão Vendedor (Bônus)",
        ...(!isSeller ? ["Comissão Líder"] : []),
        "Comissão Supervisor",
        "Cliente",
        "Contrato",
        "Status",
        "Tabela",
        "Data de criação",
        "Data de atualização",
      ];

      if (formattedData.length > 0) {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "ExportedReport");
        XLSX.utils.sheet_add_aoa(worksheet, [titles], { origin: "A1" });
        XLSX.writeFile(workbook, "transactions-reports.xlsx");
      }
    } catch (error) {
      console.error("Error exporting transactions report:", error);
      toast({
        title: "Não foi possível exportar o relatório",
        description:
          "Alguns dados não puderam ser carregados. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllData, isSeller, toast]);

  const getPageIndex = () => {
    if (!data) return 0;
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1;
  };

  const getPageCount = () => {
    if (!data) return 0;
    return Math.ceil(data.pagination.total / data.pagination.limit);
  };

  const handleNextPage = () => {
    if (!data) return;
    const newPage = data.pagination.skip / data.pagination.limit + 1;
    if (getPageCount() === getPageIndex()) return;
    const skipValue = newPage * data.pagination.limit;
    setParams((p) => ({ ...p, skip: skipValue }));
  };

  const handlePreviousPage = () => {
    if (!data) return;
    const newPage = data.pagination.skip / data.pagination.limit - 1;
    if (newPage < 0) return;
    const skipValue = newPage * data.pagination.limit;
    setParams((p) => ({ ...p, skip: skipValue }));
  };

  const filters: FiltersType[] = useMemo(() => {
    const result: FiltersType[] = ["Date", "TransactionStatus"];

    if (isAdmin)
      result.push("Managers", "Supervisors", "Leaders", "Teams", "Sellers");
    if (isManager) result.push("Supervisors", "Leaders", "Teams", "Sellers");
    if (isSupervisor) result.push("Leaders", "Teams", "Sellers");
    if (isLeader) result.push("Sellers");

    result.push("Value");

    return result;
  }, [isAdmin, isManager, isSupervisor, isLeader]);

  return {
    table,
    filters,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    isExporting,
    exportProgress,
    handleExportToExcel,
    setParams,
    params,
  };
};
